<template>
  <v-dialog
    v-model="isVisible"
    persistent
    scrollable
  >
    <v-card ref="dialogCard">
      <div ref="dialogHeader">
        <v-toolbar
          color="secondary"
          dark
          tile
        >
          <v-toolbar-title>
            {{ dialogTitle }}
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            v-if="!isLoading"
            icon
            @click="_ => onCloseDialog()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />
      </div>

      <template v-if="isLoading">
        <div
          class="d-flex align-center justify-center"
          style="height: 350px;"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            size="64"
          />
        </div>
      </template>

      <template v-else>
        <v-card-text
          ref="dialogCardContent"
          class="pa-2 ma-0"
        >
          <DocketEntryEditorInviteOverview
            ref="overview"
            @update:valid="isFormValid = $event"
          />
        </v-card-text>

        <div ref="dialogFooter">
          <v-divider />

          <v-card-actions>
            <div class="w-50 d-flex">
              <v-btn
                text
                @click="_ => onCloseDialog()"
              >
                Cancel
              </v-btn>
            </div>

            <div class="w-50 d-flex">
              <v-spacer />

              <v-btn
                color="warning"
                :disabled="!editedDocketEntryWasChanged"
                text
                @click="_ => onResetDocketEntry()"
              >
                <v-icon left>
                  mdi-restore
                </v-icon>
                Reset
              </v-btn>

              <v-btn
                color="success"
                :disabled="!canSaveDocketEntry"
                @click="_ => onSaveDocketEntry()"
              >
                <v-icon left>
                  mdi-content-save
                </v-icon>
                Save
              </v-btn>
            </div>
          </v-card-actions>
        </div>
      </template>

      <v-overlay
        :value="isSavingDocketEntry"
        absolute
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="128"
        />
      </v-overlay>
    </v-card>

    <ConfirmationDialog
      v-model="isResetDocketEntryConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to reset all data? All your changes will be lost!"
      cancel-button-label="No"
      cancel-button-color="primary"
      confirm-button-label="Yes"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="_ => resetDocketEntry()"
    />

    <ConfirmationDialog
      v-model="isCloseDialogConfirmationDialogVisible"
      title="Action Confirmation"
      message="Are you sure you want to exit? All your changes will be lost!"
      cancel-button-label="Stay"
      cancel-button-color="primary"
      confirm-button-label="Exit, don't save"
      confirm-button-color="secondary"
      invert-action-buttons
      @confirmed="_ => closeDialog()"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { isNil } from 'lodash'

import { alertHandler } from '@/alertHandler'

import { CALENDAR_NAMESPACE } from '@/store/calendar'
import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import DocketEntryEditorInviteOverview from './DocketEntryEditorInviteOverview.vue'

export default {
  name: 'DocketEntryEditorInviteDialog',

  components: {
    ConfirmationDialog,
    DocketEntryEditorInviteOverview,
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      canSaveDocketEntry: false,
      isCloseDialogConfirmationDialogVisible: false,
      isFormValid: false,
      isLoading: true,
      isResetDocketEntryConfirmationDialogVisible: false,
      isSavingDocketEntry: false,
      userClickedSave: false,
    }
  },

  computed: {
    dialogTitle () {
      if (this.isLoading) return 'Loading, please wait...'

      if (!this.docketEntryEditorDocketEntryId) {
        return `Create ${this.docketEntryEditorActionType}`
      }

      return `Edit ${this.docketEntryEditorActionType}`
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    isNewDocketEntry () {
      return !this.docketEntry.id
    },

    isTwilioConfigured () {
      return this.features?.twilio?.enabled
    },

    isVisible: {
      get () {
        return this.isDocketEntryEditorInviteVisible
      },

      set (val) {
        if (val) return
        this.closeDocketEntryEditor()
      },
    },

    legalCase () {
      return this.legalCases?.find(lc => lc.id === this.docketEntry?.legal_case_id)
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'docketEntryEditorActionType',
      'docketEntryEditorDocketEntryId',
      'editedDocketEntry',
      'editedDocketEntryWasChanged',
      'isDocketEntryEditorInviteVisible',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapState(SHARED_NAMESPACE, [
      'features',
    ]),
  },

  watch: {
    docketEntry: {
      deep: true,

      handler: function () {
        this.updateCanSaveDocketEntry()
      },
    },

    isDocketEntryEditorInviteVisible: {
      handler (value) {
        if (!value) return
        this.onEditorIsVisible()
      },

      immediate: true,
    },

    isFormValid: {
      handler: function () {
        this.updateCanSaveDocketEntry()
      },
    },
  },

  methods: {
    closeDialog () {
      this.closeDocketEntryEditor()
    },

    onCloseDialog () {
      if (this.editedDocketEntryWasChanged) {
        this.isCloseDialogConfirmationDialogVisible = true
      } else {
        this.closeDialog()
      }
    },

    async onEditorIsVisible () {
      this.isLoading = true

      await this.getFeatures()
      await this.getUsersModel()

      if (this.currentUser.atleastCommenter && !this.currentUser.isExternal) {
        await this.reloadPermissions()
      }

      await this.prepareDocketEntryData()
    },

    onResetDocketEntry () {
      if (this.editedDocketEntryWasChanged) this.isResetDocketEntryConfirmationDialogVisible = true
    },

    onSaveDocketEntry () {
      this.isSavingDocketEntry = true
      this.saveDocketEntry()
    },

    async prepareDocketEntryData () {
      this.isLoading = true

      const result = await this.getOptions({ field: 'site_timezone', model: 'SiteSetting::Timezone' })
      const timezone = result[0]?.timezone

      await this.resetEditedDocketEntryData({ timezone: timezone })

      if (!this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)) {
        await this.fetchLegalCase(this.docketEntry.legal_case_id)
      }

      await this.getDocketEntryTypes({
        action_type: 'Appointment',
        archived: false,
        legal_case_type_ids: [this.legalCase.legal_case_type_id],
      })

      await this.loadDocketEntriesInfo({ legalCaseId: this.docketEntry.legal_case_id })

      this.isLoading = false
    },

    resetDocketEntry () {
      this.resetEditedDocketEntryWithOriginalData().
        catch(error => this.displayAlert('error', error)).
        finally(_ => {
          this.isResetDocketEntryConfirmationDialogVisible = false

          if (this.isNewDocketEntry) {
            this.tabList.forEach(tab => { tab.visited = false })
            const firstTab = this.tabList.find(tab => tab.enabled)
            this.selectedTab = firstTab.value
          }
        })
    },

    saveDocketEntry () {
      this.userClickedSave = false

      this.saveEditedDocketEntry({ url: 'invites' }).
        then(_ => {
          this.setCreatedDocketEntryId(this.docketEntry.id)
          this.displayAlert('success', `${this.docketEntry.action_type} saved successfully.`)
          this.closeDialog()
        }).
        catch(error => this.displayAlert('error', null, error)).
        finally(_ => {
          this.isSavingDocketEntry = false
        })
    },

    updateCanSaveDocketEntry () {
      const requiredFieldsSet = (
        this.docketEntry.attendances?.length === 1 &&
          this.docketEntry.docket_entries_assignments?.length === 1 &&
          !isNil(this.docketEntry.docket_entry_type_id) &&
          !isNil(this.docketEntry.event_datetime_end) &&
          !isNil(this.docketEntry.event_datetime_start)
      )

      this.canSaveDocketEntry = requiredFieldsSet && this.isFormValid
    },

    ...mapActions([
      'getUsersModel',
    ]),

    ...mapActions(CALENDAR_NAMESPACE, [
      'reloadPermissions',
    ]),

    ...mapActions(DOCKET_ENTRY_NAMESPACE, [
      'closeDocketEntryEditor',
      'resetEditedDocketEntryData',
      'resetEditedDocketEntryWithOriginalData',
      'saveEditedDocketEntry',
    ]),

    ...mapActions(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'getDocketEntryTypes',
    ]),

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'fetchLegalCase',
      'loadDocketEntriesInfo',
      'setCreatedDocketEntryId',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
      'getOptions',
    ]),
  },
}
</script>
